/**
 * Constants and enums for the Advanced Retirement Calculator
 * @module domain/retirement/constants
 */

import type { ReturnRiskAssumption, RetirementGoal, CashFlowEvent } from "./types";
import { DEFAULT_PROFILE_BY_CATEGORY, findProfileById, deriveSigmaFromProfile } from "./riskProfiles";

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Asset categories for portfolio allocation and return assumptions
 */
export enum AssetCategory {
  CASH = "CASH",
  DEPOSIT = "DEPOSIT",
  STOCKS = "STOCKS",
  CRYPTO = "CRYPTO",
  RSU = "RSU",
  PENSION = "PENSION",
  REALESTATE = "REALESTATE",
  OTHER = "OTHER",
  DEBT = "DEBT",
}

/**
 * Withdrawal calculation modes
 */
export enum WithdrawalMode {
  /** Withdraw fixed amount to cover expenses (adjusted for inflation) */
  EXPENSE_BASED = "EXPENSE_BASED",
  /** Withdraw percentage of portfolio each year */
  RATE_BASED = "RATE_BASED",
}

/**
 * Life event frequency types
 */
export enum LifeEventFrequency {
  ONE_TIME = "oneTime",
  MONTHLY = "monthly",
  YEARLY = "yearly",
}

/**
 * Life event cash flow direction
 */
export enum LifeEventDirection {
  INFLOW = "inflow",
  OUTFLOW = "outflow",
}

/**
 * Life event kind - distinguishes between cashflow, buy asset, and sell asset events
 */
export enum LifeEventKind {
  CASHFLOW = "cashflow",
  BUY_ASSET = "buyAsset",
  SELL_ASSET = "sellAsset",
  LOAN = "loan",
}

/**
 * Invest from source - where the money comes from for buy asset events
 */
export enum InvestFromSource {
  /** Reduces monthlySavings (purchase comes from regular savings) */
  CASHFLOW = "cashflow",
  /** Extra money (gift, bonus) - does NOT reduce monthlySavings */
  EXTRA = "extra",
  /** Uses from accumulated buying credit pool */
  BUYING_CREDIT = "buyingCredit",
}

/**
 * Subtype for recurring cashflow events — helps classify the nature of the event.
 * For outflows: reducing income, debt repayment, or other.
 * For inflows: increasing income, yield/returns, or other.
 */
export enum CashFlowSubtype {
  /** Outflow: reducing regular income (e.g., career change, part-time) */
  REDUCE_INCOME = "reduceIncome",
  /** Outflow: debt repayment (mortgage, loan) */
  DEBT_REPAYMENT = "debtRepayment",
  /** Inflow: increasing regular income (raise, new job, side income) */
  INCREASE_INCOME = "increaseIncome",
  /** Inflow: yield/returns from investments */
  YIELD = "yield",
  /** Inflow: reduced expenses — treated as negative spending, not as income */
  REDUCE_EXPENSES = "reduceExpenses",
  /** General / other */
  OTHER = "other",
}

/**
 * Escalation type - how a recurring cashflow event changes over time
 */
export enum EscalationType {
  /** No escalation - amount stays the same every year */
  NONE = "none",
  /** Increase/decrease by a fixed percentage each year (compounds) */
  PERCENTAGE = "percentage",
  /** Increase/decrease by a fixed amount each year */
  FIXED = "fixed",
}

/**
 * End-year mode — determines how the end year of a recurring life event is resolved.
 *
 * SPECIFIC_YEAR (default):  User specifies an explicit year (or leaves blank for "indefinite").
 * AT_RETIREMENT:            Event ends at the user's target retirement age.
 * AT_PENSION:               Event ends when pension becomes eligible (67 males / 65 females in Israel).
 * AT_LIFE_EXPECTANCY:       Event lasts until the user's configured life expectancy.
 */
export enum EndYearMode {
  /** Explicit year set by the user (default / legacy behaviour) */
  SPECIFIC_YEAR = "specificYear",
  /** Ends at the user's target retirement age */
  AT_RETIREMENT = "atRetirement",
  /** Ends at pension eligibility age */
  AT_PENSION = "atPension",
  /** Ends at life expectancy */
  AT_LIFE_EXPECTANCY = "atLifeExpectancy",
}

/**
 * Sell type - how to specify the amount to sell
 */
export enum SellType {
  /** Sell a fixed amount */
  AMOUNT = "amount",
  /** Sell a percentage of the category */
  PERCENTAGE = "percentage",
}

/**
 * Retirement goal kinds — each kind defines a different success condition
 * evaluated by the Monte Carlo simulation.
 */
export enum RetirementGoalKind {
  /** Have at least targetAmount in portfolio at targetAge */
  PORTFOLIO_TARGET = "PORTFOLIO_TARGET",
  /** Portfolio stays above 0 through targetAge (default: life expectancy) */
  PORTFOLIO_SURVIVES = "PORTFOLIO_SURVIVES",
  /** Reach FIRE number by targetAge */
  FIRE_BY_AGE = "FIRE_BY_AGE",
  /** Portfolio never drops below targetAmount at any point */
  MINIMUM_FLOOR = "MINIMUM_FLOOR",
  /**
   * Purchase goal — withdraws targetAmount from portfolio at targetAge.
   * Success = portfolio can afford the purchase AND survives afterward.
   * This is an "active" goal that modifies the simulation (unlike passive checks).
   */
  PURCHASE_GOAL = "PURCHASE_GOAL",
}

/** Category tag used to identify auto-generated life events from PURCHASE_GOAL goals */
export const PURCHASE_GOAL_EVENT_TAG = "purchase_goal_auto";

// ============================================================================
// DEFAULT GOAL PRESETS
// ============================================================================

/**
 * Create the default "survive through retirement" goal.
 * Used when no goals are configured (backward compat).
 */
export function createDefaultSurviveGoal(lifeExpectancy: number): RetirementGoal {
  return {
    id: "default-survive",
    kind: RetirementGoalKind.PORTFOLIO_SURVIVES,
    name: "", // Will be resolved from translations at display time
    icon: "beach",
    enabled: true,
    targetAge: lifeExpectancy,
  };
}

/**
 * Preset goal templates that users can pick from to quickly add a goal.
 * The `name` field is intentionally empty — it is resolved from translations in the UI.
 */
export const GOAL_PRESETS: Omit<RetirementGoal, "id">[] = [
  {
    kind: RetirementGoalKind.PORTFOLIO_SURVIVES,
    name: "",
    icon: "beach",
    enabled: true,
  },
  {
    kind: RetirementGoalKind.PORTFOLIO_TARGET,
    name: "",
    icon: "home",
    enabled: true,
    targetAmount: 2_000_000,
    targetAge: 55,
  },
  {
    kind: RetirementGoalKind.FIRE_BY_AGE,
    name: "",
    icon: "trendingUp",
    enabled: true,
    targetAge: 50,
  },
  {
    kind: RetirementGoalKind.MINIMUM_FLOOR,
    name: "",
    icon: "savings",
    enabled: true,
    targetAmount: 500_000,
  },
  {
    kind: RetirementGoalKind.PURCHASE_GOAL,
    name: "",
    icon: "home",
    enabled: true,
    targetAmount: 2_000_000,
    targetAge: 55,
  },
];

/**
 * Generate life events from PURCHASE_GOAL goals.
 * Each enabled PURCHASE_GOAL becomes a one-time outflow CashFlowEvent at its target year.
 * The events are tagged with PURCHASE_GOAL_EVENT_TAG and isAutoGenerated=true so they
 * are automatically managed (not persisted, stripped on reload).
 *
 * @param goals - The user's retirement goals
 * @param currentAge - The user's current age
 * @returns Array of CashFlowEvent objects to inject into lifeEvents
 */
export function generatePurchaseGoalEvents(
  goals: RetirementGoal[],
  currentAge: number,
): CashFlowEvent[] {
  const currentYear = new Date().getFullYear();
  return goals
    .filter(g => g.enabled && g.kind === RetirementGoalKind.PURCHASE_GOAL && g.targetAge !== undefined && g.targetAmount !== undefined)
    .map((goal) => ({
      id: `purchase-goal-${goal.id}`,
      kind: LifeEventKind.CASHFLOW as const,
      name: goal.name,
      startYear: currentYear + (goal.targetAge! - currentAge),
      frequency: LifeEventFrequency.ONE_TIME as const,
      amount: goal.targetAmount!,
      direction: LifeEventDirection.OUTFLOW as const,
      enabled: true,
      categoryTag: PURCHASE_GOAL_EVENT_TAG,
      icon: goal.icon,
      isAutoGenerated: true,
      sourceProductId: goal.id,
    }));
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Helper: derive the standardDeviation for a category from its default risk profile.
 */
function defaultSigma(category: AssetCategory): number {
  const profileId = DEFAULT_PROFILE_BY_CATEGORY[category];
  const profile = findProfileById(profileId);
  return profile ? deriveSigmaFromProfile(profile) : 0;
}

/**
 * Default return and risk assumptions by category (real returns, after inflation).
 *
 * Sources & rationale for expected returns:
 * - CASH   0%:  No real return after inflation
 * - DEPOSIT 3%: Israeli bank deposits / savings plans real yield
 * - STOCKS 7%:  S&P 500 long-term real CAGR ≈ 7% (1926-2024)
 * - CRYPTO 8%:  Highly uncertain; conservative long-term estimate
 * - RSU    7%:  Same as stocks (company equity)
 * - PENSION 4%: Israeli pension funds typical real return
 * - REALESTATE 3%: Appreciation only (rental income tracked separately)
 * - OTHER  3%:  Moderate default
 *
 * standardDeviation is derived from the category's default risk profile.
 * riskProfileId points to the built-in profile for this category.
 */
export const DEFAULT_RETURN_ASSUMPTIONS: Record<AssetCategory, ReturnRiskAssumption> = {
  [AssetCategory.CASH]: {
    expectedReturn: 0,
    standardDeviation: defaultSigma(AssetCategory.CASH),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.CASH],
    description: "Cash & Checking Accounts",
  },
  [AssetCategory.DEPOSIT]: {
    expectedReturn: 0.03,
    standardDeviation: defaultSigma(AssetCategory.DEPOSIT),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.DEPOSIT],
    description: "Deposits & Savings Accounts",
  },
  [AssetCategory.STOCKS]: {
    expectedReturn: 0.07,
    standardDeviation: defaultSigma(AssetCategory.STOCKS),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.STOCKS],
    description: "Stocks & Equities",
  },
  [AssetCategory.CRYPTO]: {
    expectedReturn: 0.08,
    standardDeviation: defaultSigma(AssetCategory.CRYPTO),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.CRYPTO],
    description: "Cryptocurrencies",
  },
  [AssetCategory.RSU]: {
    expectedReturn: 0.07,
    standardDeviation: defaultSigma(AssetCategory.RSU),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.RSU],
    description: "RSU / Stock Options",
  },
  [AssetCategory.PENSION]: {
    expectedReturn: 0.04,
    standardDeviation: defaultSigma(AssetCategory.PENSION),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.PENSION],
    description: "Pension & Retirement Funds",
  },
  [AssetCategory.REALESTATE]: {
    expectedReturn: 0.03,
    standardDeviation: defaultSigma(AssetCategory.REALESTATE),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.REALESTATE],
    description: "Real Estate",
  },
  [AssetCategory.OTHER]: {
    expectedReturn: 0.03,
    standardDeviation: defaultSigma(AssetCategory.OTHER),
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.OTHER],
    description: "Other Assets",
  },
  [AssetCategory.DEBT]: {
    expectedReturn: 0,
    standardDeviation: 0,
    riskProfileId: DEFAULT_PROFILE_BY_CATEGORY[AssetCategory.DEBT] ?? "static",
    description: "Loans & Debts",
  },
};

// ============================================================================
// PENSION CONSTANTS
// ============================================================================

/**
 * Pension calculation constants
 */
export const ASSUMED_PENSION_INVESTMENT_YEARLY_RETURN = 4.31; // Percentage

/**
 * Retirement age by gender in Israel
 */
export const RETIREMENT_AGE_MALE = 67;
export const RETIREMENT_AGE_FEMALE = 65;

/**
 * Pension product types that should be excluded from retirement pension calculation
 * (קרנות השתלמות - Keren Hishtalmut is a savings fund, not a pension)
 */
export const EXCLUDED_PENSION_TYPES = [
  "קרנות השתלמות",
  "קרן השתלמות",
];

/**
 * Pension product types excluded from locked-pension tracking
 * (they are liquid or have their own eligibility logic)
 */
export const LIQUID_PENSION_PRODUCT_TYPES = [
  "קרנות השתלמות",
  "קרן השתלמות",
  "השתלמות",
  "חסכון לכל ילד",
];

/**
 * Default marginal income tax rate used for kitzba taxation when the
 * user's actual marginal rate is unknown.
 */
export const DEFAULT_MARGINAL_TAX_RATE = 0.30;

/**
 * Yearly cost rate applied on negative cash (debt) when the projection runs out of liquid assets.
 * E.g. 0.10 = 10% per year on the debt balance.
 */
export const DEBT_ANNUAL_COST_RATE = 0.10;

// ============================================================================
// STORAGE CONSTANTS
// ============================================================================

/**
 * localStorage schema version for retirement calculator data
 * Bump this when making breaking changes to persisted data structure
 *
 * v1: original schema with standardDeviation per category
 * v2: risk profiles replace standardDeviation; custom profiles stored separately
 * v3: recalibrated risk profiles with historically accurate deltas; updated default returns
 * v4: life events impact on cashflow display; inflation-adjusted pre-retirement figures
 * v5: retirement goals feature — adds goals[] to persisted state
 * v6: pension law overhaul — pension cashflow, per-product plans, mekadem, tax
 * v7: couple support — partner inputs, per-partner pension attribution (v6 payloads still load)
 */
export const STORAGE_SCHEMA_VERSION = 7;
